import React, { useState } from 'react';
import { Sun, Moon, LayoutGrid, List, Clock, CalendarClock, ArrowDownAZ, Settings, KeyRound, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import './SettingsModal.css';

const SettingsModal = ({ onClose }) => {
  const { settings, update } = useSettings();
  const { user, login } = useAuth();

  // Password change state
  const [pwSection, setPwSection] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  // Google linking state
  const [googleLinking, setGoogleLinking] = useState(false);
  const [pendingCredential, setPendingCredential] = useState(null); // for email mismatch confirm
  const [mismatchInfo, setMismatchInfo] = useState(null);

  const handleChangePassword = async () => {
    if (newPw !== confirmPw) { toast.error('New passwords do not match'); return; }
    if (newPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setPwLoading(true);
    try {
      await api.put('/api/auth/change-password', {
        currentPassword: currentPw || undefined,
        newPassword: newPw,
      });
      toast.success('Password updated!');
      setCurrentPw(''); setNewPw(''); setConfirmPw(''); setPwSection(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update password');
    } finally { setPwLoading(false); }
  };

  const handleLinkGoogle = () => {
    if (!window.google) { toast.error('Google Sign-In not available'); return; }
    setGoogleLinking(true);
    window.google.accounts.id.initialize({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      callback: async (response) => {
        try {
          const res = await api.post('/api/auth/link-google', { credential: response.credential });
          if (res.data.linked) {
            login(res.data.token, res.data.user);
            toast.success(res.data.message);
          } else if (res.data.emailMismatch) {
            setPendingCredential(response.credential);
            setMismatchInfo(res.data);
          }
        } catch (err) {
          toast.error(err.response?.data?.message || 'Failed to link Google account');
        } finally { setGoogleLinking(false); }
      },
    });
    window.google.accounts.id.prompt();
    setTimeout(() => setGoogleLinking(false), 8000); // fallback if user dismisses
  };

  const handleConfirmTransfer = async () => {
    if (!pendingCredential) return;
    setGoogleLinking(true);
    try {
      const res = await api.post('/api/auth/link-google/confirm', { credential: pendingCredential });
      login(res.data.token, res.data.user);
      toast.success(res.data.message);
      setPendingCredential(null); setMismatchInfo(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to complete linking');
    } finally { setGoogleLinking(false); }
  };

  return (
    <div className="smodal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="smodal-panel">

        <div className="smodal-header">
          <div className="smodal-header-icon"><Settings size={16} /></div>
          <div>
            <h2 className="smodal-title">Settings</h2>
            <p className="smodal-sub">Personalize your ApplAI experience</p>
          </div>
          <button className="smodal-close" onClick={onClose}>✕</button>
        </div>

        <div className="smodal-body">

          {/* Appearance */}
          <div className="smodal-section">
            <div className="smodal-section-label">Appearance</div>
            <div className="smodal-row">
              <div className="smodal-row-text">
                <div className="smodal-row-title">Theme</div>
                <div className="smodal-row-desc">Choose between light and dark interface</div>
              </div>
              <div className="smodal-pill-group">
                <button
                  className={`smodal-pill ${settings.theme === 'light' ? 'active' : ''}`}
                  onClick={() => update('theme', 'light')}
                >
                  <Sun size={13} /> Light
                </button>
                <button
                  className={`smodal-pill ${settings.theme === 'dark' ? 'active' : ''}`}
                  onClick={() => update('theme', 'dark')}
                >
                  <Moon size={13} /> Dark
                </button>
              </div>
            </div>
          </div>

          {/* Job Tracker */}
          <div className="smodal-section">
            <div className="smodal-section-label">Job Tracker</div>
            <div className="smodal-row">
              <div className="smodal-row-text">
                <div className="smodal-row-title">Default view</div>
                <div className="smodal-row-desc">Which view opens when you go to Applications</div>
              </div>
              <div className="smodal-pill-group">
                <button
                  className={`smodal-pill ${settings.defaultView === 'board' ? 'active' : ''}`}
                  onClick={() => update('defaultView', 'board')}
                >
                  <LayoutGrid size={12} /> Board
                </button>
                <button
                  className={`smodal-pill ${settings.defaultView === 'table' ? 'active' : ''}`}
                  onClick={() => update('defaultView', 'table')}
                >
                  <List size={12} /> Table
                </button>
              </div>
            </div>
            <div className="smodal-row">
              <div className="smodal-row-text">
                <div className="smodal-row-title">Default sort</div>
                <div className="smodal-row-desc">How applications are sorted when you open the tracker</div>
              </div>
              <div className="smodal-pill-group">
                <button
                  className={`smodal-pill ${settings.defaultSort === 'lastUpdateDate' ? 'active' : ''}`}
                  onClick={() => update('defaultSort', 'lastUpdateDate')}
                  title="Most recently updated first"
                >
                  <Clock size={12} /> Last updated
                </button>
                <button
                  className={`smodal-pill ${settings.defaultSort === 'applicationDate' ? 'active' : ''}`}
                  onClick={() => update('defaultSort', 'applicationDate')}
                  title="Most recently added first"
                >
                  <CalendarClock size={12} /> Date added
                </button>
                <button
                  className={`smodal-pill ${settings.defaultSort === 'company' ? 'active' : ''}`}
                  onClick={() => update('defaultSort', 'company')}
                  title="Alphabetical by company name"
                >
                  <ArrowDownAZ size={12} /> Company A–Z
                </button>
              </div>
            </div>
          </div>

          {/* Account */}
          <div className="smodal-section">
            <div className="smodal-section-label">Account</div>

            {/* Password */}
            <div className="smodal-row smodal-row-col">
              <div className="smodal-row-text">
                <div className="smodal-row-title"><KeyRound size={13} style={{verticalAlign:'middle',marginRight:5}} />Password</div>
                <div className="smodal-row-desc">
                  {user?.hasPassword ? 'Change your current password' : 'Set a password so you can also log in without Google'}
                </div>
              </div>
              {!pwSection ? (
                <button className="smodal-acct-btn" onClick={() => setPwSection(true)}>
                  {user?.hasPassword ? 'Change Password' : 'Set Password'}
                </button>
              ) : (
                <div className="smodal-pw-form">
                  {user?.hasPassword && (
                    <input className="smodal-input" type="password" placeholder="Current password"
                      value={currentPw} onChange={e => setCurrentPw(e.target.value)} />
                  )}
                  <input className="smodal-input" type="password" placeholder="New password (min 6 chars)"
                    value={newPw} onChange={e => setNewPw(e.target.value)} />
                  <input className="smodal-input" type="password" placeholder="Confirm new password"
                    value={confirmPw} onChange={e => setConfirmPw(e.target.value)} />
                  <div className="smodal-pw-actions">
                    <button className="smodal-acct-btn smodal-acct-ghost" onClick={() => { setPwSection(false); setCurrentPw(''); setNewPw(''); setConfirmPw(''); }}>Cancel</button>
                    <button className="smodal-acct-btn smodal-acct-primary" onClick={handleChangePassword} disabled={pwLoading}>
                      {pwLoading ? 'Saving...' : 'Save Password'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Google */}
            <div className="smodal-row">
              <div className="smodal-row-text">
                <div className="smodal-row-title"><Link2 size={13} style={{verticalAlign:'middle',marginRight:5}} />Google Account</div>
                <div className="smodal-row-desc">
                  {user?.googleId ? 'Your account is connected to Google — you can sign in with Google' : 'Connect Google so you can sign in without a password'}
                </div>
              </div>
              {user?.googleId ? (
                <span className="smodal-connected-badge">Connected</span>
              ) : (
                <button className="smodal-acct-btn smodal-acct-google" onClick={handleLinkGoogle} disabled={googleLinking}>
                  {googleLinking ? 'Waiting...' : 'Connect Google'}
                </button>
              )}
            </div>

            {/* Email mismatch confirmation */}
            {mismatchInfo && (
              <div className="smodal-mismatch-banner">
                <p className="smodal-mismatch-msg">{mismatchInfo.message}</p>
                <p className="smodal-mismatch-sub">
                  Your account email will be updated to <strong>{mismatchInfo.googleEmail}</strong> and Google will be linked. Your applications and data stay the same.
                </p>
                <div className="smodal-pw-actions">
                  <button className="smodal-acct-btn smodal-acct-ghost" onClick={() => { setPendingCredential(null); setMismatchInfo(null); }}>Cancel</button>
                  <button className="smodal-acct-btn smodal-acct-primary" onClick={handleConfirmTransfer} disabled={googleLinking}>
                    {googleLinking ? 'Updating...' : `Update email & connect`}
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>

        <div className="smodal-footer">
          <span className="smodal-footer-note">Settings are saved locally to this browser.</span>
          <button className="smodal-done-btn" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
